import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import { FiEdit, FiTrash, FiPlus } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';


function formatDateFr(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
}

const emptyAbsence = (user) => ({
  inscription_id: '',
  periode_id: '',
  date_absence: new Date().toISOString().slice(0, 10),
  journee_entiere: 'N',
  incident_id: '',
  motif: '',
  dmd_constat: 'Constaté',
  user_id: user?.id || ''
});

const AbsencesPage = () => {
  const { user } = useAuth();
  const [absences, setAbsences] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyAbsence(user));
  const [search, setSearch] = useState('');
  const [incidents, setIncidents] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);

  useEffect(() => {
    fetchAbsences();
    api.get('/incidents').then(res => setIncidents(res.data.data || []));
    api.get('/periodes').then(res => setPeriodes(res.data.data || []));
    api.get('/inscriptions').then(res => setInscriptions(res.data.data || []));
  }, []);

  const fetchAbsences = async () => {
    const res = await api.get('/absences');
    setAbsences(res.data.data || []);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyAbsence(user));
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      inscription_id: item.inscription_id || '',
      periode_id: item.periode_id || '',
      date_absence: item.date_absence || new Date().toISOString().slice(0, 10),
      journee_entiere: item.journee_entiere || 'N',
      incident_id: item.incident_id || '',
      motif: item.motif || '',
      dmd_constat: item.dmd_constat || 'Constaté',
      user_id: user?.id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette absence ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/absences/${id}`);
      fetchAbsences();
      Swal.fire('Supprimé !', 'L\'absence a été supprimée.', 'success');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyAbsence(user));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.inscription_id || !form.periode_id || !form.date_absence) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Les champs obligatoires doivent être remplis.' });
      return;
    }
    const dataToSend = { ...form, user_id: user?.id };
    if (editing) {
      await api.put(`/absences/${editing.id}`, dataToSend);
    } else {
      await api.post('/absences', dataToSend);
    }
    fetchAbsences();
    setShowModal(false);
  };

  // Pagination locale
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const filteredAbsences = absences.filter(a =>
    (a.motif?.toLowerCase().includes(search.toLowerCase()) ||
     a.dmd_constat?.toLowerCase().includes(search.toLowerCase()) ||
     String(a.date_absence).includes(search) ||
     String(a.UserId).includes(search))
  );
  const totalPages = Math.max(1, Math.ceil(filteredAbsences.length / pageSize));
  const paginatedAbsences = filteredAbsences.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Absences</h1>
        <button
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={handleAdd}
        >
          <FiPlus />
          <span>Nouvelle absence</span>
        </button>
      </div>
      {/* Formulaire de recherche */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-4">
        <input
          type="text"
          placeholder="Recherche par motif, date, utilisateur..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border rounded px-3 py-2 w-full"
        />
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Élève</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Période</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Journée entière</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Incident</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Motif</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Constat</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAbsences.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">Aucune absence</td>
              </tr>
            ) : (
              paginatedAbsences.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                    {inscriptions.find(i => i.id === item.inscription_id)?.nom || item.inscription_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                    {periodes.find(p => p.id === item.periode_id)?.periode || item.periode_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{formatDateFr(item.date_absence)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.journee_entiere === 'Y' ? 'Oui' : 'Non'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                    {incidents.find(i => i.id === item.incident_id)?.designation || ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.motif}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{item.dmd_constat}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      className="p-2 text-primary-600 hover:text-primary-900 mr-2 rounded-full"
                      onClick={() => handleEdit(item)}
                      title="Modifier"
                    >
                      <FiEdit size={18} />
                    </button>
                    <button
                      className="p-2 text-red-600 hover:text-red-900 rounded-full"
                      onClick={() => handleDelete(item.id)}
                      title="Supprimer"
                    >
                      <FiTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination locale harmonisée */}
      {!showModal && totalPages > 1 && (
        <div className="flex justify-center items-center gap-1 mt-4 flex-wrap pb-4">
          <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 text-sm">Premier</button>
          <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 text-sm">Précédent</button>
          {(() => {
            const pages = [];
            if (totalPages <= 10) {
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              for (let i = 1; i <= 5; i++) pages.push(i);
              if (page > 7 && page < totalPages - 6) {
                pages.push('...');
                for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                pages.push('...');
              } else {
                pages.push('...');
              }
              for (let i = totalPages - 4; i <= totalPages; i++) {
                if (i > 5) pages.push(i);
              }
            }
            return [...new Set(pages)].map((p, idx) =>
              p === '...'
                ? <span key={"ellipsis-" + idx} className="mx-0.5 px-2 text-gray-400 dark:text-gray-500">...</span>
                : <button
                    key={p}
                    className={`px-2 py-1 rounded mx-0.5 text-sm ${p === page ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 hover:bg-primary-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'}`}
                    onClick={() => setPage(p)}
                    disabled={p === page}
                  >{p}</button>
            );
          })()}
          <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 text-sm">Suivant</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 text-sm">Dernier</button>
        </div>
      )}

      {/* Modal ajout/mise à jour */}
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalClose}
        shouldCloseOnOverlayClick={false}
        contentLabel="Absence"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.75)',
            zIndex: 1000
          },
          content: {
            maxWidth: '800px',
            width: '100%',
            margin: 'auto',
            borderRadius: 12,
            padding: 0,
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            backgroundColor: document.documentElement.classList.contains('dark') ? '#1F2937' : '#fff',
            color: document.documentElement.classList.contains('dark') ? '#E5E7EB' : '#111827'
          }
        }}
      >
        <div className="flex justify-between items-center px-6 pt-4 pb-2 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-gray-100">{editing ? 'Modifier' : 'Nouvelle'} absence</h2>
          <button
            type="button"
            onClick={handleModalClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-2xl font-bold focus:outline-none"
            title="Fermer"
          >×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 dark:bg-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Élève *</label>
              <select
                name="inscription_id"
                value={form.inscription_id}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner...</option>
                {inscriptions.map(i => (
                  <option key={i.id} value={i.id}>{i.nom} {i.prenom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Période *</label>
              <select
                name="periode_id"
                value={form.periode_id}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner...</option>
                {periodes.map(p => (
                  <option key={p.id} value={p.id}>{p.periode}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Date *</label>
              <input
                type="date"
                name="date_absence"
                value={form.date_absence}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div className="flex items-center mt-6">
              <input
                type="checkbox"
                id="journee_entiere"
                name="journee_entiere"
                checked={form.journee_entiere === 'Y'}
                onChange={handleChange}
                className="mr-2 dark:accent-primary-600"
              />
              <label htmlFor="journee_entiere" className="text-sm font-medium dark:text-gray-200">Journée entière</label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Incident associé</label>
              <select
                name="incident_id"
                value={form.incident_id}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Aucun</option>
                {incidents.map(i => (
                  <option key={i.id} value={i.id}>{i.designation}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Motif</label>
              <input
                type="text"
                name="motif"
                value={form.motif}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Constat</label>
              <input
                type="text"
                name="dmd_constat"
                value={form.dmd_constat}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={handleModalClose} className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600">Annuler</button>
            <button type="submit" className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">{editing ? 'Enregistrer' : 'Créer'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AbsencesPage;
